<template>
  <div class="documentos-evento">
    <div class="documentos-header">
      <div>
        <h3>Documentos descargables</h3>
        <p>
          Agrega documentos para que aparezcan como botones descargables en la vista de lista de usuarios.
        </p>
      </div>
    </div>

    <div
      class="drop-zone"
      :class="{ 'is-dragging': isDragging, 'is-uploading': uploading }"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="fileInput"
        type="file"
        class="hidden-input"
        multiple
        :accept="acceptTypes"
        @change="onFileInputChange"
      />

      <div class="drop-icon">⇩</div>
      <div class="drop-title">Arrastra documentos aquí</div>
      <div class="drop-subtitle">o usa el botón para seleccionarlos desde tu equipo.</div>

      <button type="button" class="btn-upload" :disabled="uploading" @click="openFilePicker">
        {{ uploading ? 'Subiendo...' : 'Agregar documento' }}
      </button>
      <div class="drop-hint">PDF, Word, Excel, PowerPoint, TXT, CSV e imágenes. Máximo 25 MB por archivo.</div>
    </div>

    <div v-if="loading" class="state-box">Cargando documentos...</div>

    <div v-else-if="!documentos.length" class="state-box empty">
      Todavía no hay documentos configurados para este evento.
    </div>

    <div v-else class="documents-list">
      <div class="documents-head">
        <span>Archivo</span>
        <span>Texto del botón visible al usuario</span>
      </div>

      <div v-for="(doc, index) in documentos" :key="doc.id_evento_documento" class="document-row">
        <div class="document-file">
          <span class="order-pill">{{ index + 1 }}</span>
          <div class="file-info">
            <a v-if="doc.download_url" :href="doc.download_url" target="_blank" rel="noopener" class="file-name">
              {{ doc.nombre_original }}
            </a>
            <span v-else class="file-name">{{ doc.nombre_original }}</span>
            <small>{{ formatFileMeta(doc) }}</small>
          </div>
        </div>

        <div class="document-actions">
          <input
            v-model="doc.etiqueta_boton"
            class="label-input"
            type="text"
            placeholder="Ej: Descargar programa"
            @input="hasChanges = true"
          />
          <button type="button" class="btn-danger" :disabled="saving || deletingId === doc.id_evento_documento" @click="deleteDocumento(doc)">
            {{ deletingId === doc.id_evento_documento ? 'Eliminando...' : 'Eliminar' }}
          </button>
        </div>
      </div>

      <div class="documents-footer">
        <button type="button" class="btn-secondary" :disabled="loading || uploading" @click="loadDocumentos">
          Recargar
        </button>
        <button type="button" class="btn-save" :disabled="saving || !hasChanges" @click="saveLabels">
          {{ saving ? 'Guardando...' : 'Guardar textos' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="js">
import { ref, computed, onMounted, watch, defineProps } from 'vue'
import { useToast } from 'vue-toastification'

const props = defineProps({
  evento: { type: Object, required: true },
  baseUrl: { type: String, default: '/api' }
})

const toast = useToast()
const fileInput = ref(null)
const documentos = ref([])
const loading = ref(false)
const uploading = ref(false)
const saving = ref(false)
const deletingId = ref(null)
const hasChanges = ref(false)
const isDragging = ref(false)
const dragDepth = ref(0)

const acceptTypes = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp'

const eventoToken = computed(() => props.evento?.token || '')

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.documentos)) return payload.documentos
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

async function loadDocumentos() {
  if (!eventoToken.value) return
  loading.value = true
  try {
    const res = await fetch(`${props.baseUrl}/eventos/${encodeURIComponent(eventoToken.value)}/documentos`)
    const data = await res.json()
    if (!res.ok || data?.ok === false) {
      throw new Error(data?.msg || data?.message || 'No se pudieron cargar los documentos.')
    }
    documentos.value = normalizeResponse(data).map((doc, idx) => ({
      ...doc,
      orden: Number(doc.orden) > 0 ? Number(doc.orden) : idx + 1,
      etiqueta_boton: doc.etiqueta_boton || doc.nombre_original || `Documento ${idx + 1}`
    }))
    hasChanges.value = false
  } catch (error) {
    console.error(error)
    toast.error(error.message || 'No se pudieron cargar los documentos del evento.')
  } finally {
    loading.value = false
  }
}

function openFilePicker() {
  if (uploading.value) return
  fileInput.value?.click()
}

function onFileInputChange(event) {
  const files = Array.from(event?.target?.files || [])
  uploadFiles(files)
  if (event?.target) event.target.value = ''
}

function onDragEnter() {
  dragDepth.value += 1
  isDragging.value = true
}

function onDragOver() {
  isDragging.value = true
}

function onDragLeave() {
  dragDepth.value = Math.max(0, dragDepth.value - 1)
  if (dragDepth.value === 0) isDragging.value = false
}

function onDrop(event) {
  dragDepth.value = 0
  isDragging.value = false
  const files = Array.from(event?.dataTransfer?.files || [])
  uploadFiles(files)
}

async function uploadFiles(files = []) {
  const validFiles = files.filter(Boolean)
  if (!validFiles.length || !eventoToken.value) return

  uploading.value = true
  try {
    for (const file of validFiles) {
      const formData = new FormData()
      formData.append('documento', file)

      const res = await fetch(`${props.baseUrl}/eventos/${encodeURIComponent(eventoToken.value)}/documentos/upload`, {
        method: 'POST',
        body: formData
      })
      const data = await res.json()
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.msg || data?.message || `No se pudo subir ${file.name}.`)
      }
    }

    toast.success(validFiles.length === 1 ? 'Documento agregado correctamente.' : 'Documentos agregados correctamente.')
    await loadDocumentos()
  } catch (error) {
    console.error(error)
    toast.error(error.message || 'No se pudieron subir los documentos.')
  } finally {
    uploading.value = false
  }
}

async function saveLabels() {
  if (!eventoToken.value) return
  saving.value = true
  try {
    const payload = {
      documentos: documentos.value.map((doc, idx) => ({
        id_evento_documento: doc.id_evento_documento,
        etiqueta_boton: String(doc.etiqueta_boton || doc.nombre_original || `Documento ${idx + 1}`).trim(),
        orden: idx + 1,
        activo: doc.activo === false || doc.activo === 0 || doc.activo === '0' ? 0 : 1
      }))
    }

    const res = await fetch(`${props.baseUrl}/eventos/${encodeURIComponent(eventoToken.value)}/documentos/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const data = await res.json()
    if (!res.ok || data?.ok === false) {
      throw new Error(data?.msg || data?.message || 'No se pudieron guardar los textos.')
    }

    toast.success('Textos de documentos guardados correctamente.')
    await loadDocumentos()
  } catch (error) {
    console.error(error)
    toast.error(error.message || 'No se pudieron guardar los documentos.')
  } finally {
    saving.value = false
  }
}

async function deleteDocumento(doc) {
  const ok = window.confirm(`¿Eliminar el documento "${doc.nombre_original}"?`)
  if (!ok || !eventoToken.value) return

  deletingId.value = doc.id_evento_documento
  try {
    const res = await fetch(`${props.baseUrl}/eventos/${encodeURIComponent(eventoToken.value)}/documentos/delete/${doc.id_evento_documento}`, {
      method: 'POST'
    })
    const data = await res.json()
    if (!res.ok || data?.ok === false) {
      throw new Error(data?.msg || data?.message || 'No se pudo eliminar el documento.')
    }

    toast.success('Documento eliminado correctamente.')
    await loadDocumentos()
  } catch (error) {
    console.error(error)
    toast.error(error.message || 'No se pudo eliminar el documento.')
  } finally {
    deletingId.value = null
  }
}

function formatFileMeta(doc) {
  const parts = []
  if (doc.extension) parts.push(String(doc.extension).toUpperCase())
  const size = Number(doc.tamano_bytes || 0)
  if (size > 0) {
    if (size >= 1024 * 1024) parts.push(`${(size / (1024 * 1024)).toFixed(1)} MB`)
    else if (size >= 1024) parts.push(`${Math.round(size / 1024)} KB`)
    else parts.push(`${size} B`)
  }
  return parts.join(' · ')
}

watch(eventoToken, () => loadDocumentos(), { immediate: false })
onMounted(loadDocumentos)
</script>

<style scoped lang="scss">
.documentos-evento {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1rem;
  background: #fff;
}

.documentos-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  h3 {
    font-size: 1.2rem;
    font-weight: 800;
    color: #111827;
    margin: 0 0 0.25rem;
  }

  p {
    color: #64748b;
    margin: 0;
    font-size: 0.92rem;
  }
}

.drop-zone {
  border: 2px dashed #cbd5e1;
  border-radius: 1rem;
  padding: 1.25rem;
  text-align: center;
  background: #f8fafc;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}

.drop-zone.is-dragging {
  border-color: #2563eb;
  background: #eff6ff;
  transform: translateY(-1px);
}

.drop-zone.is-uploading {
  opacity: 0.75;
}

.hidden-input {
  display: none;
}

.drop-icon {
  width: 44px;
  height: 44px;
  margin: 0 auto 0.5rem;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  font-weight: 900;
}

.drop-title {
  font-weight: 800;
  color: #0f172a;
}

.drop-subtitle,
.drop-hint {
  color: #64748b;
  font-size: 0.88rem;
  margin-top: 0.25rem;
}

.btn-upload,
.btn-save,
.btn-secondary,
.btn-danger {
  border: none;
  border-radius: 0.65rem;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.15s ease, opacity 0.15s ease;
}

.btn-upload:hover,
.btn-save:hover,
.btn-secondary:hover,
.btn-danger:hover {
  transform: translateY(-1px);
}

.btn-upload {
  margin-top: 0.85rem;
  padding: 0.65rem 1rem;
  color: #fff;
  background: #2563eb;
}

.btn-save {
  padding: 0.65rem 1rem;
  color: #fff;
  background: #16a34a;
}

.btn-save:disabled,
.btn-upload:disabled,
.btn-secondary:disabled,
.btn-danger:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  padding: 0.65rem 1rem;
  color: #111827;
  background: #e5e7eb;
}

.btn-danger {
  min-width: 88px;
  padding: 0.6rem 0.8rem;
  color: #fff;
  background: #dc2626;
}

.state-box {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.85rem;
  background: #f8fafc;
  color: #475569;
  text-align: center;
  border: 1px solid #e5e7eb;
}

.state-box.empty {
  border-style: dashed;
}

.documents-list {
  margin-top: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  overflow: hidden;
}

.documents-head,
.document-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(260px, 1fr);
  gap: 1rem;
  align-items: center;
}

.documents-head {
  background: #f1f5f9;
  color: #334155;
  font-weight: 800;
  font-size: 0.88rem;
  padding: 0.8rem 1rem;
}

.document-row {
  padding: 0.9rem 1rem;
  border-top: 1px solid #e5e7eb;
}

.document-file,
.document-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.order-pill {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  flex: 0 0 auto;
}

.file-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.file-name {
  color: #1d4ed8;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-name:hover {
  text-decoration: underline;
}

.file-info small {
  color: #64748b;
}

.label-input {
  width: 100%;
  padding: 0.65rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.65rem;
  outline: none;
}

.label-input:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.35);
}

.documents-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.7rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .documents-head {
    display: none;
  }

  .document-row {
    grid-template-columns: 1fr;
  }

  .document-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
